import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

interface TermsOfServiceProps {
  onNavigate?: (route: string) => void;
}

export function TermsOfService({ onNavigate }: TermsOfServiceProps) {
  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto bg-card p-8 rounded-lg shadow border">
<div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => window.history.back()}
            style={{paddingLeft:"0"}}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <p className="text-sm text-muted-foreground mb-1"><strong>Source Code Control Limited</strong></p>
          <p className="text-sm text-muted-foreground mb-4"><em>ORbiT8 - Cloud Software Supply Chain Scanning</em></p>
          <h1 className="text-3xl font-bold text-foreground mb-2">Terms &amp; Conditions</h1>
          <h2 className="text-xl font-semibold text-foreground mb-4">ORbiT8 Platform Terms of Use</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Effective Date: April 2026 | Version 1.0
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm border border-border">
              <tbody>
                <tr className="border-b border-border">
                  <td className="px-4 py-2 font-bold bg-muted">Company</td>
                  <td className="px-4 py-2">Source Code Control Limited</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-4 py-2 font-bold bg-muted">Product</td>
                  <td className="px-4 py-2">ORbiT8</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-4 py-2 font-bold bg-muted">Website</td>
                  <td className="px-4 py-2">
                    <a href="https://www.orbit8.co" className="text-primary hover:underline">orbit8.co</a>; <a href="https://sourcecodecontrol.com" className="text-primary hover:underline">sourcecodecontrol.com</a>
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-4 py-2 font-bold bg-muted">Contact</td>
                  <td className="px-4 py-2">
                    <a href="mailto:open@sourcecodecontrol.com" className="text-primary hover:underline">open@sourcecodecontrol.com</a>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-bold bg-muted">Registered Office</td>
                  <td className="px-4 py-2">153 Mortimer Street, Herne Bay, Kent, CT6 5HA, United Kingdom</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-muted border border-border rounded p-4 mb-8">
            <p className="text-sm italic">
              <strong>IMPORTANT:</strong> Please read this document carefully before using ORbiT8. By accessing or using the platform, you agree to be bound by these terms.
            </p>
          </div>

          <div className="space-y-6 text-foreground/80">

            <hr className="my-8 border-border" />

            <section id="introduction">
              <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introduction and Agreement</h2>
              <p>
                These Terms and Conditions ("Terms") govern your access to and use of ORbiT8, a cloud-based software supply chain scanning platform ("Platform") operated by Source Code Control Limited ("we", "us", "our", "the Company"), a company registered in England and Wales with its registered office in Reading, Berkshire, United Kingdom.
              </p>
              <p className="mt-3">
                By registering for, accessing, or using ORbiT8, you ("User", "you", "your") agree to be bound by these Terms. If you are using ORbiT8 on behalf of an organisation, you represent that you have the authority to bind that organisation to these Terms, and references to "you" include that organisation.
              </p>
              <p className="mt-3">If you do not agree to these Terms, you must not use the Platform.</p>
            </section>

            <hr className="my-8 border-border" />

            <section id="definitions">
              <h2 className="text-2xl font-semibold text-foreground mb-4">2. Definitions</h2>
              <p>In these Terms, the following definitions apply:</p>
              <div className="space-y-2 mt-3">
                <p><strong>"Platform"</strong> means the ORbiT8 cloud-based software supply chain scanning tool, including all features, functionality, APIs, and interfaces provided by Source Code Control Limited.</p>
                <p><strong>"User"</strong> means any individual or organisation that accesses or uses the Platform.</p>
                <p><strong>"Account"</strong> means the registered account created by a User to access the Platform.</p>
                <p><strong>"Content"</strong> means any code, data, files, repositories, or other materials submitted to the Platform by a User.</p>
                <p><strong>"Scan Results"</strong> means the output generated by the Platform following analysis of User Content, including but not limited to licence compliance reports, vulnerability assessments, and Software Bill of Materials (SBOM) data.</p>
                <p><strong>"Subscription"</strong> means the applicable paid or trial access tier under which a User accesses the Platform.</p>
                <p><strong>"Intellectual Property Rights"</strong> means all patents, trade marks, service marks, copyrights, database rights, trade secrets, and other intellectual property rights.</p>
              </div>
            </section>

            <hr className="my-8 border-border" />

            <section id="platform-description">
              <h2 className="text-2xl font-semibold text-foreground mb-4">3. Platform Description</h2>
              <p>
                ORbiT8 is a cloud-hosted software supply chain scanning tool designed to help organisations identify, analyse, and manage risks associated with open source software components. The Platform provides, without limitation:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-3">
                <li>Automated scanning of code repositories and software packages to detect open source components and third-party dependencies.</li>
                <li>Licence compliance analysis, identifying potential intellectual property risks arising from the use of open source software.</li>
                <li>Security vulnerability detection, cross-referencing components against known vulnerability databases including CVE and NVD.</li>
                <li>Software Bill of Materials (SBOM) generation in industry-standard formats.</li>
                <li>OpenChain and ISO/IEC 5230:2020 compliance support and reporting.</li>
                <li>Risk scoring and remediation guidance for identified issues.</li>
              </ul>
              <p className="mt-3">The Platform is currently made available as a test and evaluation service. Features and functionality may change without notice during this period.</p>
            </section>

            <hr className="my-8 border-border" />

            <section id="account-registration">
              <h2 className="text-2xl font-semibold text-foreground mb-4">4. Account Registration and Access</h2>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">4.1 Registration</h3>
              <p>
                To use the Platform, you must create an Account by providing accurate, complete, and current information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your Account.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">4.2 Eligibility</h3>
              <p>
                You must be at least 18 years of age and have the legal capacity to enter into a binding agreement to use the Platform. Use of the Platform by minors is not permitted.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">4.3 Organisational Accounts</h3>
              <p>
                Where an Account is created on behalf of an organisation, the individual registering confirms they have appropriate authority to do so. The organisation assumes responsibility for all use of the Account by its employees and representatives.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">4.4 Account Security</h3>
              <p>
                You must notify us immediately at <a href="mailto:open@sourcecodecontrol.co" className="text-primary hover:underline">open@sourcecodecontrol.co</a> if you become aware of any unauthorised use of your Account or any breach of security. We are not liable for any loss arising from unauthorised use of your Account.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="acceptable-use">
              <h2 className="text-2xl font-semibold text-foreground mb-4">5. Acceptable Use</h2>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">5.1 Permitted Use</h3>
              <p>
                You may use the Platform solely for lawful purposes and in accordance with these Terms. You agree to use the Platform only to scan software for which you have appropriate rights or permissions.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">5.2 Prohibited Activities</h3>
              <p>You must not:</p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Upload or submit any code, data, or material that you do not have the right to use or that infringes any third-party rights.</li>
                <li>Use the Platform to scan software belonging to third parties without their express consent.</li>
                <li>Attempt to reverse engineer, decompile, or disassemble any part of the Platform.</li>
                <li>Use the Platform in any manner that could damage, disable, overburden, or impair it.</li>
                <li>Attempt to gain unauthorised access to any part of the Platform or its underlying systems.</li>
                <li>Use automated tools, bots, or scrapers to access the Platform without our written consent.</li>
                <li>Use the Platform for any unlawful purpose or in violation of any applicable laws or regulations.</li>
                <li>Introduce viruses, malicious code, or other harmful material to the Platform.</li>
                <li>Represent Scan Results as legal advice or rely on them as a substitute for qualified legal counsel.</li>
              </ul>
            </section>

            <hr className="my-8 border-border" />

            <section id="ip">
              <h2 className="text-2xl font-semibold text-foreground mb-4">6. Intellectual Property</h2>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">6.1 Platform Ownership</h3>
              <p>
                All Intellectual Property Rights in the Platform, including its software, technology, design, and documentation, are owned by or licensed to Source Code Control Limited. Nothing in these Terms transfers any such rights to you.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">6.2 User Content</h3>
              <p>
                You retain all Intellectual Property Rights in the Content you submit to the Platform. By submitting Content, you grant us a limited, non-exclusive, royalty-free licence to process that Content solely for the purpose of providing the Platform services to you.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">6.3 Scan Results</h3>
              <p>
                Scan Results generated from your Content are provided to you under a non-exclusive licence for your internal business use. You may not redistribute, resell, or sublicense Scan Results without our prior written consent.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">6.4 Feedback</h3>
              <p>
                If you provide us with feedback, suggestions, or ideas about the Platform, you grant us an unrestricted, perpetual, royalty-free right to use that feedback for any purpose without obligation to you.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="fees">
              <h2 className="text-2xl font-semibold text-foreground mb-4">7. Subscriptions, Fees, and Payment</h2>
              <p>
                Access to ORbiT8 is currently provided on a test and evaluation basis. Where paid subscriptions are introduced, we will notify you in advance and publish applicable pricing. Continued use of the Platform following the introduction of paid tiers will constitute acceptance of the relevant fees.
              </p>
              <p className="mt-3">
                All fees, where applicable, will be stated inclusive or exclusive of VAT as indicated, and payment terms will be set out in the applicable order form or subscription agreement.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="confidentiality">
              <h2 className="text-2xl font-semibold text-foreground mb-4">8. Confidentiality</h2>
              <p>
                Each party agrees to keep confidential any information designated as confidential or which ought reasonably to be understood as confidential, and not to disclose such information to any third party without prior written consent, except as required by law.
              </p>
              <p className="mt-3">
                We will treat your Content and Scan Results as confidential information. We will not access your Content except as necessary to provide the Platform services or as required by law.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="data-protection">
              <h2 className="text-2xl font-semibold text-foreground mb-4">9. Data Protection</h2>
              <p>
                Our collection and use of personal data in connection with the Platform is described in our Privacy Policy and GDPR Policy, both of which are incorporated into these Terms by reference. By using the Platform, you acknowledge and agree to the processing of personal data as described in those documents.
              </p>
              <p className="mt-3">
                Where you submit Content that contains personal data, you are responsible for ensuring that you have a lawful basis for doing so and that such processing complies with applicable data protection laws.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="liability">
              <h2 className="text-2xl font-semibold text-foreground mb-4">10. Disclaimers and Limitation of Liability</h2>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">10.1 Disclaimer of Warranties</h3>
              <p className="uppercase">
                THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE". TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p className="mt-3">
                We do not warrant that the Platform will be uninterrupted, error-free, or free of harmful components. Scan Results are provided for informational purposes only and do not constitute legal, security, or compliance advice.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">10.2 Limitation of Liability</h3>
              <p className="uppercase">
                TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, SOURCE CODE CONTROL LIMITED SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE PLATFORM.
              </p>
              <p className="mt-3">
                Our total aggregate liability to you under these Terms shall not exceed the greater of (a) the total fees paid by you in the twelve months preceding the event giving rise to the claim, or (b) £100.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">10.3 Exceptions</h3>
              <p>
                Nothing in these Terms limits or excludes our liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded by law.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="indemnification">
              <h2 className="text-2xl font-semibold text-foreground mb-4">11. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Source Code Control Limited and its officers, directors, employees, and agents from any claims, damages, losses, and expenses (including reasonable legal fees) arising from: (a) your use of the Platform; (b) your breach of these Terms; (c) your Content; or (d) your violation of any applicable law or third-party rights.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="termination">
              <h2 className="text-2xl font-semibold text-foreground mb-4">12. Term and Termination</h2>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">12.1 Term</h3>
              <p>These Terms apply from the date you first access the Platform and continue until terminated by either party.</p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">12.2 Termination by You</h3>
              <p>
                You may stop using the Platform and close your Account at any time by contacting us at <a href="mailto:open@sourcecodecontrol.co" className="text-primary hover:underline">open@sourcecodecontrol.co</a>.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">12.3 Termination by Us</h3>
              <p>
                We may suspend or terminate your access to the Platform immediately and without notice if: (a) you breach these Terms; (b) we are required to do so by law; or (c) we decide to discontinue the Platform.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">12.4 Effect of Termination</h3>
              <p>
                On termination, your right to access the Platform ceases immediately. Provisions that by their nature should survive termination (including intellectual property, confidentiality, disclaimers, and limitation of liability) shall survive.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="changes">
              <h2 className="text-2xl font-semibold text-foreground mb-4">13. Changes to the Platform and Terms</h2>
              <p>
                We reserve the right to modify, suspend, or discontinue any aspect of the Platform at any time without notice, particularly during the test and evaluation phase.
              </p>
              <p className="mt-3">
                We may update these Terms from time to time. We will notify registered users of material changes by email or through the Platform. Your continued use of the Platform after such notification constitutes acceptance of the updated Terms.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="governing-law">
              <h2 className="text-2xl font-semibold text-foreground mb-4">14. Governing Law and Dispute Resolution</h2>
              <p>
                These Terms are governed by the laws of England and Wales. Any dispute arising from or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="general">
              <h2 className="text-2xl font-semibold text-foreground mb-4">15. General</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>These Terms constitute the entire agreement between you and Source Code Control Limited regarding the Platform.</li>
                <li>If any provision of these Terms is found unenforceable, the remaining provisions continue in full force.</li>
                <li>Our failure to enforce any right under these Terms does not constitute a waiver of that right.</li>
                <li>You may not assign your rights or obligations under these Terms without our prior written consent.</li>
                <li>We may assign our rights and obligations to any successor entity or affiliate.</li>
              </ul>
            </section>

            <hr className="my-8 border-border" />

            <div className="bg-muted p-4 rounded-md">
              <h2 className="text-xl font-semibold text-foreground mb-3">Contact</h2>
              <p>For questions regarding these Terms, please contact: <a href="mailto:open@sourcecodecontrol.co" className="text-primary hover:underline">open@sourcecodecontrol.co</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
